import React, { useRef, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { Sprite, Container } from "@pixi/react";
import { useButtonHover } from "../../../../Hooks/useButtonHover";
import { SOUND_NAME } from "../../../../sounds";
import { getUITexture } from "../../../../assets";
import { useDimensions } from "../../../../Contexts/DimensionsContext";
import { PixiSettings } from "../../../../pixiSettings";
import { soundManager } from "../../../../SoundManager";
import { useDispatch, useSelector } from "react-redux";
import {
  openAutoplayPopup,
  closeAutoplayPopup,
  stopAutoPlay,
  closeMenu,
} from "../../../../State/actions";
import {
  selectAutoPlayActive,
  selectAutoplayPopupActive,
  selectIsSpinning,
  selectCriticalAnimationPlaying,
  selectHoldAndWinActive,
  selectUserInterfaceDisabled,
  selectFreeSpinActive,
  selectRoyalCollectActive,
} from "../../../../State/selectors";

export const AUTOPLAY_BUTTON_CONFIG = {
  BASE_WIDTH: 80,
  BASE_HEIGHT: 80,
  DEVICE_CONFIGS: {
    [PixiSettings.DEVICE.MOBILE_SMALL]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: {
        deviceMultiplier: 1.965,
      },
      [PixiSettings.ORIENTATION.LANDSCAPE]: {
        deviceMultiplier: 1.45,
      },
    },
    [PixiSettings.DEVICE.MOBILE]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: {
        deviceMultiplier: 1.965,
      },
      [PixiSettings.ORIENTATION.LANDSCAPE]: {
        deviceMultiplier: 1.6,
      },
    },
    [PixiSettings.DEVICE.TABLET]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: {
        deviceMultiplier: 2.3,
      },
      [PixiSettings.ORIENTATION.LANDSCAPE]: {
        deviceMultiplier: 1.7,
      },
    },
    [PixiSettings.DEVICE.DESKTOP]: {
      deviceMultiplier: 1.0,
    },
  },
};

// Position config — extracted for easy tweaking
const AUTOPLAY_POSITION_CONFIG = {
  [PixiSettings.DEVICE.MOBILE_SMALL]: {
    [PixiSettings.ORIENTATION.PORTRAIT]: {
      x: (toolbar, buttonConfig) =>
        toolbar.width / 2 - buttonConfig.width * 1.7,
      y: (toolbar, buttonConfig, dimensions) => -dimensions.height / 11,
    },
    [PixiSettings.ORIENTATION.LANDSCAPE]: {
      x: (toolbar, buttonConfig) => toolbar.width - buttonConfig.width * 1.03,
      y: (toolbar, buttonConfig, dimensions) =>
        -dimensions.height / 2 + buttonConfig.height * 2.5,
    },
  },
  [PixiSettings.DEVICE.MOBILE]: {
    [PixiSettings.ORIENTATION.PORTRAIT]: {
      x: (toolbar, buttonConfig) =>
        toolbar.width / 2 - buttonConfig.width * 1.7,
      y: (toolbar, buttonConfig, dimensions) => -dimensions.height / 11,
    },
    [PixiSettings.ORIENTATION.LANDSCAPE]: {
      x: (toolbar, buttonConfig) => toolbar.width - buttonConfig.width * 1.03,
      y: (toolbar, buttonConfig, dimensions) =>
        -dimensions.height / 2 + buttonConfig.height * 2.5,
    },
  },
  [PixiSettings.DEVICE.TABLET]: {
    [PixiSettings.ORIENTATION.PORTRAIT]: {
      x: (toolbar, buttonConfig) => toolbar.width - buttonConfig.width * 0.95,
      y: (toolbar, buttonConfig, dimensions) =>
        toolbar.height - buttonConfig.height * 5.05,
    },
    [PixiSettings.ORIENTATION.LANDSCAPE]: {
      x: (toolbar, buttonConfig) => toolbar.width - buttonConfig.width * 1.05,
      y: (toolbar, buttonConfig) =>
        toolbar.height / 2 - buttonConfig.height * 3.325,
    },
  },
  [PixiSettings.DEVICE.DESKTOP]: {
    x: (toolbar, buttonConfig) => toolbar.width - buttonConfig.width * 3.95,
    y: (toolbar, buttonConfig) => toolbar.height / 2,
  },
};

const ButtonAutoplay = ({ toolbar }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { getResponsiveConfig, device, orientation, dimensions } =
    useDimensions();

  // Redux state
  const autoPlayActive = useSelector(selectAutoPlayActive);
  const popupActive = useSelector(selectAutoplayPopupActive);
  const isSpinning = useSelector(selectIsSpinning);
  const criticalAnimationPlaying = useSelector(selectCriticalAnimationPlaying);
  const holdAndWinActive = useSelector(selectHoldAndWinActive);
  const userInterfaceDisabled = useSelector(selectUserInterfaceDisabled);
  const freeSpinActive = useSelector(selectFreeSpinActive);
  const royalCollectActive = useSelector(selectRoyalCollectActive);

  // Scale hover + press effect
  const {
    hoverContainerRef,
    buttonRef,
    handlePointerOver,
    handlePointerOut,
    triggerPress,
  } = useButtonHover();

  // Debounce refs
  const isProcessingClickRef = useRef(false);
  const debounceTimeoutRef = useRef(null);

  // Autoplay textures
  const defaultTexture = useMemo(() => getUITexture("icon_autoplay"), []);
  const hoverTexture = useMemo(() => getUITexture("icon_autoplay_hover"), []);

  // Show hover texture when autoplay is active (as "active" indicator)
  const activeTexture = autoPlayActive ? hoverTexture : defaultTexture;

  // Custom hover handlers that swap to hover texture
  const onPointerOver = () => {
    handlePointerOver();
    if (buttonRef.current && hoverTexture) {
      buttonRef.current.texture = hoverTexture;
    }
  };

  const onPointerOut = () => {
    handlePointerOut();
    // Revert to active texture (hover if autoplay active, default otherwise)
    if (buttonRef.current && activeTexture) {
      buttonRef.current.texture = activeTexture;
    }
  };

  // Disable button when: critical animation is playing OR (spin is active AND autoplay is not active) OR hold and win is active
  const isDisabled =
    userInterfaceDisabled ||
    criticalAnimationPlaying ||
    (isSpinning && !autoPlayActive) ||
    holdAndWinActive ||
    freeSpinActive ||
    royalCollectActive;

  // Get responsive configuration from context
  const buttonConfig = useMemo(
    () => getResponsiveConfig(AUTOPLAY_BUTTON_CONFIG, true),
    [getResponsiveConfig],
  );

  // Calculate responsive button position
  const buttonPosition = useMemo(() => {
    const deviceConfig = AUTOPLAY_POSITION_CONFIG[device];
    const posConfig = deviceConfig?.[orientation] ?? deviceConfig;
    return {
      x: posConfig.x(toolbar, buttonConfig, dimensions),
      y: posConfig.y(toolbar, buttonConfig, dimensions),
    };
  }, [device, orientation, toolbar, buttonConfig, dimensions]);

  const handleClick = () => {
    dispatch(closeMenu());

    if (isDisabled) return;

    if (isProcessingClickRef.current) return;

    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    isProcessingClickRef.current = true;

    soundManager.play(SOUND_NAME.button_click);
    triggerPress();

    if (autoPlayActive) {
      dispatch(stopAutoPlay());
    } else {
      if (popupActive) {
        dispatch(closeAutoplayPopup());
      } else {
        dispatch(openAutoplayPopup());
      }
    }

    debounceTimeoutRef.current = setTimeout(() => {
      isProcessingClickRef.current = false;
    }, 500);
  };

  // Sync texture when autoplay state changes
  useEffect(() => {
    if (buttonRef.current && activeTexture) {
      buttonRef.current.texture = activeTexture;
    }
  }, [autoPlayActive, activeTexture, buttonRef]);

  // Manage alpha when button becomes disabled
  useEffect(() => {
    if (buttonRef.current) {
      buttonRef.current.alpha = isDisabled ? 0.7 : 1;
    }
  }, [isDisabled, buttonRef]);

  // Clean up debounce timeout on unmount
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  return (
    <Container
      name="secondary_button_autoplay"
      alpha={!holdAndWinActive ? 1 : 0.001}
    >
      <Container x={buttonPosition.x} y={buttonPosition.y}>
        {/* Hover container */}
        <Container ref={hoverContainerRef}>
          <Sprite
            ref={buttonRef}
            texture={activeTexture}
            name="button_autoplay_icon"
            anchor={0.5}
            width={buttonConfig.width}
            height={buttonConfig.height}
            x={0}
            y={0}
            eventMode={isDisabled ? "none" : "static"}
            cursor={isDisabled ? "default" : "pointer"}
            alpha={isDisabled ? 0.5 : 1}
            onpointertap={handleClick}
            onpointerover={onPointerOver}
            onpointerout={onPointerOut}
            resolution={2}
            accessible={true}
            accessibleTitle={t("autoplay_title", { ns: "common" })}
          />
        </Container>
      </Container>
    </Container>
  );
};

export default ButtonAutoplay;
